import { useMemo, useState } from "react";
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import { Link } from "react-router-dom";
import { ventrisConfig } from '../ventris.config';
import { focusFirstInvalidField } from '../accessibility/focus';
import AsyncStatus from '../components/AsyncStatus';

import logo from '../assets/img/logo.jpg';

type LoginProps = {
    portal: string;
};

function Login({ portal: _portal }: LoginProps) {
  const ssoLoginEnabled = ventrisConfig.showSsoLogin;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [error, setError] = useState("");
  const [validated, setValidated] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaError, setMfaError] = useState("");
  const [ssoStep, setSsoStep] = useState(false);
  const [ssoCode, setSsoCode] = useState("");
  const [ssoError, setSsoError] = useState("");
  const [ssoLoading, setSsoLoading] = useState(false);

  const maskedEmail = useMemo(() => {
    const trimmed = email.trim();
    const domain = trimmed.split("@")[1];
    if (!domain) {
      return "***";
    }
    return `***@${domain}`;
  }, [email]);
  
  const requestMfa = async (username: string, userPassword: string) => {
    const response = await fetch(ventrisConfig.url + '/api/mfa', {
      method: 'post',
      headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: username,
        password: userPassword
      })
    });

    return response.json();
  };

  const authenticate = async (username: string, userPassword: string, code: string) => {
    const response = await fetch(ventrisConfig.url + '/api/auth-mfa', {
      method: 'post',
      headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: username,
        password: userPassword,
        mfa_code: code
      })
    });

    return response.json();
  };

  const handleSubmit = async (event: any) => {
    const form = event.currentTarget;

    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false) {
        setValidated(true);
        setError("");
        try {
          const data = await requestMfa(email, password);
          if (data.success) {
            setValidated(false);
            setMfaStep(true);
          } else {
            setError(data.message || "You entered an invalid email or password. Please try again!");
          }
        } catch (mfaRequestError) {
          console.error(mfaRequestError);
        }
    }
  };

  const handleVerifySubmit = async (event: any) => {
    const form = event.currentTarget;

    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false) {
      setValidated(true);
      setMfaError("");
      try {
        const data = await authenticate(email, password, mfaCode);
        if (data.success) {
          let token = data.token;
          sessionStorage.setItem("token", token);
          const response = await fetch(ventrisConfig.url + '/api/account', {
            method: 'get',
            headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token
            }
          });
          const accountData = await response.json();
          let currentUser: any = accountData;
          sessionStorage.setItem("portal", currentUser.role.toLowerCase());
          sessionStorage.setItem("current_user", JSON.stringify(currentUser));
          document.location.href = "/";
        } else {
          setMfaError("We couldn't sign you in. Please verify your password and code, then try again.");
        }
      } catch (verifyError) {
        console.error(verifyError);
      }
    }
  };

  const handleSendNewCode = async () => {
    setMfaError("");
    try {
      const data = await requestMfa(email, password);
      if (!data.success) {
        setMfaError(data.message || "Please wait before requesting another verification code.");
      }
    } catch (resendError) {
      console.error(resendError);
    }
  };

  const handleSsoSubmit = async (event: any) => {
    const form = event.currentTarget;

    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity() === false) {
      setValidated(true);
      focusFirstInvalidField(form);
      return;
    }

    if (form.checkValidity() !== false) {
      setValidated(true);
      setSsoError("");
      setSsoLoading(true);

      try {
        const response = await fetch(ventrisConfig.url + '/api/sso/authorize', {
          method: 'post',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            short_code: ssoCode
          })
        });

        const data = await response.json();
        if (data.success && data.redirect_url) {
          document.location.href = data.redirect_url;
          return;
        }

        setSsoError(data.message || "This SSO short code is invalid. Please check and try again.");
      } catch (authorizeError) {
        console.error(authorizeError);
        setSsoError("An error occurred while starting SSO sign in. Please try again.");
      } finally {
        setSsoLoading(false);
      }
    }
  };

  return (
    <Container fluid as="main" id="main-content" tabIndex={-1} className="login-wrapper" aria-busy={ssoLoading}>
      <AsyncStatus busy={ssoLoading} busyMessage="Starting SSO sign in." completeMessage="SSO sign-in request completed." />
      <Row className="login-row">
        <Col className="login-card">
          <div className="login-card-inner">
            <div className="login-card-header">
              <img src={logo} alt="Ventris Learning Logo" className="login-logo" />
            </div>
            <div className="login-card-divider" />
            <div className="login-card-body">
              {mfaStep ? (
                <>
                  <h1 className="login-title">Account Verification</h1>
                  <p className="login-subtitle" id="login-mfa-instructions">
                    Please enter the code that was sent to you.
                    <br />
                    This code will expire in 5 minutes.
                  </p>
                  <Form className="login-form" noValidate validated={validated} onSubmit={handleVerifySubmit}>
                    <Form.Group className="mb-2 ventris-form-element" controlId="verificationCode">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">VERIFICATION CODE</Form.Label>
                        <span className="login-label-hint">
                          Code expired?{" "}
                          <button type="button" className="login-link login-link-button" onClick={handleSendNewCode}>Send new code.</button>
                        </span>
                      </div>
                      <div className="login-input-field">
                        <Form.Control
                          required
                          type="text"
                          autoComplete="one-time-code"
                          inputMode="numeric"
                          aria-describedby={mfaError ? "login-mfa-instructions login-mfa-error" : "login-mfa-instructions"}
                          aria-invalid={!!mfaError}
                          placeholder="Enter the 6-Digit Code"
                          value={mfaCode}
                          onChange={(event) => setMfaCode(event.target.value)}
                          className={mfaError ? "login-input-error" : ""}
                        />
                        {mfaError ? (
                          <span className="login-input-error-icon" aria-hidden="true">!</span>
                        ) : null}
                      </div>
                      <Form.Control.Feedback type="invalid">Please enter a code.</Form.Control.Feedback>
                      {mfaError ? <div role="alert" className="login-input-error-text" id="login-mfa-error">{mfaError}</div> : null}
                    </Form.Group>
                    <div className="login-actions">
                      <Button
                        type="submit"
                        className={`login-btn-primary${mfaCode ? " login-btn-primary-active" : ""}`}
                        disabled={!mfaCode}
                      >
                        Verify Code
                      </Button>
                      <Link className="login-btn-secondary" to="/">
                        Cancel &amp; Return to Login
                      </Link>
                    </div>
                  </Form>
                </>
              ) : ssoStep && ssoLoginEnabled ? (
                <>
                  <h1 className="login-title">Single Sign-On</h1>
                  <p className="login-subtitle" id="sso-short-code-instructions">Enter your organization's SSO short code and continue.</p>
                  <Form className="login-form" noValidate validated={validated} onSubmit={handleSsoSubmit}>
                    <Form.Group className="mb-3 ventris-form-element" controlId="ssoShortCode">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">SSO SHORT CODE</Form.Label>
                      </div>
                      <Form.Control
                        required
                        type="text"
                        aria-describedby={ssoError ? "sso-short-code-instructions sso-short-code-error" : "sso-short-code-instructions"}
                        aria-invalid={!!ssoError}
                        placeholder="Enter SSO Short Code"
                        value={ssoCode}
                        onChange={(event) => setSsoCode(event.target.value)}
                      />
                      <Form.Control.Feedback type="invalid">Please enter an SSO short code.</Form.Control.Feedback>
                    </Form.Group>
                    { ssoError !== "" ? <Alert id="sso-short-code-error" variant="danger" className="login-error">{ssoError}</Alert> : null }
                    <div className="login-actions">
                      <Button
                        type="submit"
                        className={`login-btn-primary${ssoCode ? " login-btn-primary-active" : ""}`}
                        disabled={!ssoCode || ssoLoading}
                      >
                        {ssoLoading ? "Please wait..." : "Next"}
                      </Button>
                      <Button
                        type="button"
                        className="login-btn-secondary login-btn-secondary-button"
                        onClick={() => {
                          setSsoStep(false);
                          setSsoCode("");
                          setSsoError("");
                          setValidated(false);
                        }}
                      >
                        Cancel &amp; Return to Login
                      </Button>
                    </div>
                  </Form>
                </>
              ) : (
                <>
                  <h1 className="login-title">Welcome!</h1>
                  <p className="login-subtitle">Enter your username and password to log in.</p>
                  <Form className="login-form" noValidate validated={validated} onSubmit={handleSubmit}>
                    <Form.Group className="mb-3 ventris-form-element" controlId="email">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">USERNAME</Form.Label>
                        <span className="login-label-hint" id="login-username-hint">Your username is your email address.</span>
                      </div>
                      <Form.Control required type="email" autoComplete="username" aria-describedby="login-username-hint" placeholder="Enter Username" value={email} onChange={(event) => setEmail(event.target.value)} />
                      <Form.Control.Feedback type="invalid">Please enter an email.</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-2 ventris-form-element" controlId="password">
                      <div className="login-label-row">
                        <Form.Label className="form-label-text">PASSWORD</Form.Label>
                        <Link className="login-link" to="/reset-password">Forgot password? Click here to reset.</Link>
                      </div>
                      <Form.Control required type="password" autoComplete="current-password" placeholder="Enter Password" value={password} onChange={(event) => setPassword(event.target.value)} />
                      <Form.Control.Feedback type="invalid">Please enter a password.</Form.Control.Feedback>
                    </Form.Group>
                    { error !== "" ? <Alert variant="danger" className="login-error">{error}</Alert> : null }
                    <div className="login-actions">
                      <Button type="submit" className="login-btn-primary">Log In</Button>
                      <Link className="login-btn-secondary" to="/onboarding">
                        Register a New Account
                      </Link>
                    </div>
                    {ssoLoginEnabled ? (
                      <div className="login-sso">
                        <Button
                          type="button"
                          className="login-btn-sso"
                          onClick={() => {
                            setSsoStep(true);
                            setSsoCode("");
                            setSsoError("");
                            setValidated(false);
                          }}
                        >
                          Sign In with SSO
                        </Button>
                      </div>
                    ) : null}
                  </Form>
                </>
              )}
            </div>
            <div className="login-card-divider" />
            <div className="login-card-footer">
              <span className="login-help-text">
                <strong>Need help?</strong> Contact us by email at{" "}
                <a className="login-help-link" href="mailto:help@ventrislearning.com">help@ventrislearning.com</a>{" "}
                or by phone at{" "}
                <a className="login-help-link" href="tel:800-555-1234">800-555-1234</a>.
              </span>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default Login;
